import { useEffect, useState } from "react";
import { supabase } from "./supabase";

// O histórico da CONSULTORIA (000351) — zero regra, nenhum `select`.
//
// As seis tabelas têm `revoke all`: a leitura sai pelas duas funções do banco, que casam o
// chamado com o aluno pelo id do Bubble (o do perfil e os apelidos). A tela não decide de quem
// é nada, e não exige acesso vigente: o histórico é do aluno, como o acervo (000281).

/** Um bloco da conversa, como a carga o gravou (`etl/bbcode-conversa.mjs`). */
export type BlocoDaConsultoria =
  | { tipo: "paragrafo" | "titulo" | "codigo"; texto: string }
  | { tipo: "lista"; itens: string[]; ordenada?: boolean }
  | { tipo: "imagem"; caminho?: string; perdida?: boolean }
  | { tipo: "arquivo"; caminho?: string; perdida?: boolean; mime?: string; bytes?: number; nome?: string };

export type ChamadoDaLista = {
  id: string;
  departamento: string;
  criado_em: string;
  encerrado: boolean;
  respondido: boolean;
  mensagens: number;
  ultima_em: string | null;
  duvida: string | null;
  nota: number | null;
};

export type ChamadoInteiro = {
  chamado: {
    id: string;
    departamento: string;
    criado_em: string;
    encerrado: boolean;
    conhecimento: string | null;
    formacao: string | null;
    profissao: string | null;
  };
  duvida: Array<{ blocos: BlocoDaConsultoria[]; criado_em: string }>;
  triagem: Array<{ pergunta: string; blocos: BlocoDaConsultoria[] }>;
  // `automatica`: a saudação que o Bubble mandava em nome do atendente (000352) — não é resposta.
  mensagens: Array<{ de_atendente: boolean; automatica?: boolean; blocos: BlocoDaConsultoria[]; criado_em: string }>;
  avaliacoes: Array<{ nota: number; comentario: string | null; criado_em: string }>;
};

export async function lerMeusChamados(): Promise<ChamadoDaLista[]> {
  const { data, error } = await supabase.rpc("consultoria_meus_chamados");
  if (error) throw error;
  return ((data as { chamados?: ChamadoDaLista[] } | null)?.chamados ?? []) as ChamadoDaLista[];
}

/** O chamado inteiro, ou `null` quando ele não existe ou não é de quem pergunta — o banco não diz qual dos dois. */
export async function lerChamado(id: string): Promise<ChamadoInteiro | null> {
  const { data, error } = await supabase.rpc("consultoria_chamado", { p_id: id });
  if (error) throw error;
  const r = data as ({ ok: true } & ChamadoInteiro) | { ok: false; motivo: string } | null;
  if (!r || !r.ok) return null;
  return r;
}

// Quem tem chamado vê a porta no menu da conta. A resposta vale pela vida da página: o
// histórico é uma carga única, e entrar ou sair do «ver como» recarrega a página.
let temConsultoria: Promise<boolean> | null = null;

export function useTemConsultoria(): boolean {
  const [tem, setTem] = useState(false);
  useEffect(() => {
    let ativo = true;
    temConsultoria ??= lerMeusChamados()
      .then((l) => l.length > 0)
      .catch(() => {
        // Falha de leitura esconde a porta, e a próxima tela tenta de novo: o menu não pode
        // deixar de abrir por causa dela.
        temConsultoria = null;
        return false;
      });
    void temConsultoria.then((v) => {
      if (ativo) setTem(v);
    });
    return () => {
      ativo = false;
    };
  }, []);
  return tem;
}

/** Os caminhos de arquivo dos blocos, para assinar de uma vez. */
export function caminhosDosBlocos(blocos: BlocoDaConsultoria[]): string[] {
  return blocos.flatMap((b) =>
    (b.tipo === "imagem" || b.tipo === "arquivo") && b.caminho && !b.perdida ? [b.caminho] : [],
  );
}

/**
 * O bucket é PRIVADO: as imagens da conversa saem por URL assinada, todas num pedido só. Uma
 * hora de validade, porque a conversa é lida devagar; a que falhar some da lista, e a tela diz
 * no lugar dela que não abriu.
 */
export async function assinarImagens(caminhos: string[]): Promise<Map<string, string>> {
  const unicos = [...new Set(caminhos)];
  if (!unicos.length) return new Map();
  const { data, error } = await supabase.storage.from("consultoria").createSignedUrls(unicos, 3600);
  if (error || !data) return new Map();
  return new Map(data.flatMap((d) => (d.path && d.signedUrl && !d.error ? [[d.path, d.signedUrl] as [string, string]] : [])));
}

/** O anexo, assinado no CLIQUE e com o nome de baixar. */
export async function urlDoAnexoDaConsultoria(caminho: string, nome: string): Promise<string | null> {
  const { data, error } = await supabase.storage.from("consultoria").createSignedUrl(caminho, 300, { download: nome });
  if (error) return null;
  return data?.signedUrl ?? null;
}

/** "10/01/2026 · 09:00", no relógio de São Paulo. */
export function dataEHora(iso: string): string {
  const d = new Date(iso);
  const dia = d.toLocaleDateString("pt-BR", { timeZone: "America/Sao_Paulo" });
  const hora = d.toLocaleTimeString("pt-BR", { timeZone: "America/Sao_Paulo", hour: "2-digit", minute: "2-digit" });
  return `${dia} · ${hora}`;
}

/** O tamanho do anexo para a pessoa ler ("1,2 MB", "340 kB"). */
export function tamanho(bytes: number | undefined): string | null {
  if (!bytes) return null;
  if (bytes >= 1024 * 1024) return `${(bytes / 1024 / 1024).toLocaleString("pt-BR", { maximumFractionDigits: 1 })} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} kB`;
}
